import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import { Component } from '@angular/core';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { ConnectedAccountModel } from '@app/core/model/connected-account-model';
import { UserModel } from '@app/core/model/user.model';
import { ConnectedAccountsListService } from '@app/core/service/connected-accounts-list.service';
import { LocalStorageService } from '@app/core/service/local-storage.service';
import { TenantRouteContextService } from '@app/core/service/tenant-route-context.service';
import { TenantService } from '@app/core/service/tenant.service';
import { buildTenantEntryUrl } from '@app/core/utils/tenant-route';
import { UserEntityService } from '@app/core/service/user-entity.service';
import { ITenantFeatures } from '@backend/models/types/tenant';
import { LoadingController, MenuController } from '@ionic/angular';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-user-switcher',
  templateUrl: './user-switcher.component.html',
  styleUrls: ['./user-switcher.component.scss'],
  animations: [
    trigger('submenuInOut', [
      state(
        'in',
        style({
          height: '*'
        })
      ),
      state(
        'out',
        style({
          height: '0px'
        })
      ),
      transition('in => out', animate('250ms cubic-bezier(0.22, 1, 0.36, 1)')),
      transition('out => in', animate('250ms cubic-bezier(0.22, 1, 0.36, 1)'))
    ])
  ],
  standalone: false
})
export class UserSwitcherComponent {
  protected myConnectedAccounts: {
    email: string;
    accounts: ConnectedAccountModel[];
  }[] = [];
  protected currentUser$: Observable<UserModel> = this._userEntityService.user$;
  protected expandUserSwitcher = false;
  protected tenantFeatures: Pick<ITenantFeatures, 'connectedAccounts'> = {
    connectedAccounts: false
  };
  protected tenantName = '';

  public constructor(
    private readonly _loadingCtrl: LoadingController,
    private readonly _connectedAccountsListService: ConnectedAccountsListService,
    private readonly _authenticationService: AuthenticationService,
    private readonly _userEntityService: UserEntityService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _tenantRouteContext: TenantRouteContextService,
    private readonly _tenantService: TenantService,
    private readonly _menuController: MenuController
  ) {
    this._tenantService.tenant$.subscribe((tenant) => {
      this.tenantFeatures = tenant.features;
      this.tenantName = tenant.name;
    });

    this._connectedAccountsListService.myConnectedAccounts$.subscribe(
      (myConnectedAccounts) => {
        const groups: {
          email: string;
          accounts: ConnectedAccountModel[];
        }[] = [];

        const allAccounts = myConnectedAccounts.sort((x, y) => {
          return !!x.parent === !!y.parent ? 0 : x.parent ? 1 : -1;
        });

        allAccounts.map((account) => {
          let group = groups.find((g) => g.email === account.email);
          if (!group) {
            group = { email: account.email, accounts: [] };
            groups.push(group);
          }
          group.accounts.push(account);
        });

        this.myConnectedAccounts = groups;
      }
    );
  }

  protected addAccount() {
    this._authenticationService.logout({ addConnectedAccount: true });
  }

  protected async removeAccount(
    group: {
      email: string;
      accounts: ConnectedAccountModel[];
    },
    event: Event
  ) {
    event.stopPropagation();

    const userId = group.accounts.find((acc) => !acc.parent)._id;

    const loading = await this._loadingCtrl.create({
      message: 'Loading...'
    });
    loading.present();

    this._connectedAccountsListService
      .removeMyConnectedAccount(userId)
      .subscribe(() => {
        loading.dismiss();
      });
  }

  protected async changeAccount(userId: string, tenantId: string) {
    this.expandUserSwitcher = false;
    this._tenantRouteContext.startTenantSwitch();
    await this._menuController.close();

    this._authenticationService.logout({
      loginToConnectedAccount: userId,
      navigateTo: buildTenantEntryUrl(tenantId)
    });
  }

  protected get isMultiUserModeActive(): boolean {
    return !!this._localStorageService.getItem('multiUserMode');
  }

  protected isGroupActive(
    group: {
      email: string;
      accounts: ConnectedAccountModel[];
    },
    currentUser: UserModel
  ): boolean {
    return !!group.accounts.find((acc) => acc._id === currentUser._id);
  }
}
